import { useState } from "react";
import styled from "styled-components";
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Button,
  Textarea,
  Text,
} from "suomifi-ui-components";
import { IconRemove } from "suomifi-icons";
import TuhoavaButton from "./TuhoavaButton";
import { useModaaliBackdropSulku } from "../utils/modaaliBackdropSulku";
import { useTranslation } from "react-i18next";

interface Props {
  otsikko: string;
  kuvaus: string;
  visible: boolean;
  onConfirm: (peruste: string) => void;
  onCancel: () => void;
  /** Jos true, peruste-kenttä on vapaaehtoinen (workshop D10: Poista käyttäjä
   * ja Lukitse käyttäjätunnus eivät vaadi perustetta — virkailija voi
   * vahvistaa myös tyhjällä). Oletus: false (peruste pakollinen). */
  perusteVapaaehtoinen?: boolean;
  /** Tuhoava vahvistus: vahvistuspainike renderöidään primary-delete-
   * tyylillä (täyteväritetty punainen) ja teksti on "Poista käyttäjä".
   * Käytetään käyttäjän poiston vahvistukseen. */
  tuhoava?: boolean;
  isLoading?: boolean;
}

const Description = styled(Text)`
  margin-bottom: ${({ theme }) => theme.spacing.m};
`;

export default function PerusteModal({
  otsikko,
  kuvaus,
  visible,
  onConfirm,
  onCancel,
  perusteVapaaehtoinen,
  tuhoava,
  isLoading = false,
}: Props) {
  const { t, i18n } = useTranslation(["kayttajaKortti", "common"]);
  const [peruste, setPeruste] = useState("");
  const [virhe, setVirhe] = useState(false);
  useModaaliBackdropSulku(visible, onCancel);

  async function handleConfirm() {
    if (!perusteVapaaehtoinen && peruste.trim().length < 10) {
      setVirhe(true);
      return;
    }
    await onConfirm(peruste);
    setPeruste("");
    setVirhe(false);
  }

  function handleCancel() {
    setPeruste("");
    setVirhe(false);
    onCancel();
  }

  return (
    <Modal
      appElementId="root"
      visible={visible}
      onEscKeyDown={handleCancel}
      scrollable={false}
    >
      <ModalContent>
        <ModalTitle>{otsikko}</ModalTitle>
        <Description>{kuvaus}</Description>
        <Textarea
          labelText={
            perusteVapaaehtoinen
              ? t("peruste.vapaaehtoinen")
              : t("peruste.pakollinen")
          }
          hintText={t("vinkki.audit_trail")}
          value={peruste}
          onChange={(e) => {
            setPeruste(e.target.value);
            if (e.target.value.trim().length >= 10) setVirhe(false);
          }}
          status={virhe ? "error" : "default"}
          statusText={
            virhe ? "Peruste on pakollinen (vähintään 10 merkkiä)" : undefined
          }
          fullWidth
          rows={3}
        />
      </ModalContent>
      <ModalFooter>
        {tuhoava ? (
          <TuhoavaButton
            tuhoavaVariantti="primary"
            icon={<IconRemove />}
            onClick={handleConfirm}
          >
            Poista käyttäjä
          </TuhoavaButton>
        ) : (
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className={tuhoava ? "danger" : ""}
          >
            {isLoading
              ? t("common:lahettaa")
              : tuhoava
              ? t("poista_kayttaja")
              : t("vahvista")}
          </Button>
        )}
        <Button variant="secondary" onClick={handleCancel}>
          Peruuta
        </Button>
      </ModalFooter>
    </Modal>
  );
}
